import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useLocalStorageState } from 'ahooks';
import classNames from 'classnames';
import { Button, Divider, Dropdown, Radio, Skeleton } from 'react-daisyui';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { NIL } from 'uuid';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import { VITE_TIKTOK_FEATURE } from 'src/constants/envs';
import { useWorkspaceContext } from '../../../providers/WorkspaceProvider/Workspace.context';
import { eChannelType } from '../../../providers/SocketsProvider/Sockets.enums';
import { integrationIcons } from 'src/constants/channel.constants';
import ActiveFilterBadge from '../../ActiveFilterBadge/ActiveFilterBadge.component';
import { eDateFilterType } from '../../../providers/ModalsProvider/Modals.types';
import { eFilters } from '../AdvanceSearchModal.enum';
import { useAdvanceSearchModalContext } from '../AdvanceSearchModal.hook';
import '../AdvanceSearchModal.styles.scss';

export default function AdvanceFilters() {
  const {
    stateFilters,
    assignedUserFilters,
    funnelForFilters,
    stageForFilters,
    channelFilter,
    funnels,
    dateRange,
    dateFilterType,
    stages,
    loadingStages,
    areFiltersActive,
    activeFilterCount,
    setStages,
    setDateRange,
    setDateFilterType,
    setStageForFilters,
    setFunnelForFilters,
    setStateFilters,
    setAssignedUserFilters,
    setChannelFilter,
    getStages,
    resetFilters,
  } = useAdvanceSearchModalContext();
  const { workspaceInfo } = useWorkspaceContext();
  const { canUserAccessWhatsAppApi } = useACLContext();
  const { hasTiktokIntegration } = useIntegrationsContext();
  const { t } = useTranslation('navbar/navbar');

  const [theme] = useLocalStorageState<string | undefined>('leadsales', {
    listenStorageChange: true,
  });

  const isDarkMode = theme !== 'leadsales';
  const [startDate, endDate] = dateRange;
  const funnelDropdownTitle = funnelForFilters?.name || t('filters.all');
  const stageDropdownTitle = stageForFilters?.name || t('filters.all');
  const leadAssignedUserDropdownTitle =
    assignedUserFilters?.name || t('filters.all');

  const advanceFiltersClassName = classNames(
    'AdvanceSearchModal__AdvanceFilters',
    {
      'bg-base-100': isDarkMode,
    },
  );

  type tChannelOption = {
    value: eChannelType;
    iconSrc: string;
    label: string;
  };

  const whatsappOption: tChannelOption = canUserAccessWhatsAppApi()
    ? {
      value: eChannelType.WHATSAPP_API,
      iconSrc: integrationIcons[eChannelType.WHATSAPP_API],
      label: 'WhatsApp Business API',
    }
    : {
      value: eChannelType.WHATSAPP_WEB,
      iconSrc: integrationIcons[eChannelType.WHATSAPP_WEB],
      label: 'WhatsApp',
    };

  const channelOptions: tChannelOption[] = [
    whatsappOption,
    {
      value: eChannelType.MESSENGER,
      iconSrc: integrationIcons[eChannelType.MESSENGER],
      label: 'Messenger',
    },
    {
      value: eChannelType.INSTAGRAM,
      iconSrc: integrationIcons[eChannelType.INSTAGRAM],
      label: 'Instagram',
    },
    ...(VITE_TIKTOK_FEATURE && hasTiktokIntegration()
      ? [{
        value: eChannelType.TIKTOK,
        iconSrc: integrationIcons[eChannelType.TIKTOK],
        label: 'TikTok',
      }]
      : []),
  ];

  function selectChannel(value: eChannelType | null) {
    setChannelFilter(value);
  }

  return (
    <div className={advanceFiltersClassName}>
      <div className="flex items-center gap-2">
        <h3 className="AdvanceSearchModal__AdvanceFilters__Title">
          {t('filters.title')}
        </h3>
        <ActiveFilterBadge count={activeFilterCount} />
      </div>
      <Button
        className="rounded input-bordered"
        size="sm"
        variant="outline"
        disabled={!areFiltersActive}
        onClick={resetFilters}
      >
        {t('filters.clearFilters')}
      </Button>
      <div className="form-control gap-1">
        <label className="label-text font-sans font-bold">
          {t('advanceSearch.leadsStatus')}
        </label>
        <div className="flex gap-3 py-1">
          <div
            className="flex items-center gap-2"
            onClick={() => {
              setStateFilters(null);
            }}
          >
            <Radio
              color="primary"
              name="messages"
              size="sm"
              checked={!stateFilters}
              readOnly
            />
            <label className="label-text-alt">{t('filters.all')}</label>
          </div>
          <div
            className="flex items-center gap-2"
            onClick={() => {
              setStateFilters(eFilters.ACTIVE);
            }}
          >
            <Radio
              color="primary"
              name="messages"
              size="sm"
              checked={stateFilters === eFilters.ACTIVE}
              readOnly
            />
            <label className="label-text-alt">
              {t('advanceSearch.active')}
            </label>
          </div>
          <div
            className="flex items-center gap-2"
            onClick={() => {
              setStateFilters(eFilters.ARCHIVE);
            }}
          >
            <Radio
              color="primary"
              name="messages"
              size="sm"
              value="archived"
              checked={stateFilters === eFilters.ARCHIVE}
              readOnly
            />
            <label className="label-text-alt">
              {t('advanceSearch.archive')}
            </label>
          </div>
        </div>
      </div>
      <Divider className="m-0" />
      <div className="form-control gap-1">
        <label className="label-text font-sans font-bold">
          {t('filters.integration')}
        </label>
        <div className="form-control gap-3">
          <div
            className="flex items-center gap-2"
            onClick={() => selectChannel(null)}
          >
            <Radio
              color="primary"
              name="advanceSearchChannel"
              size="sm"
              checked={!channelFilter}
              readOnly
            />
            <label className="label-text-alt">{t('filters.allFem')}</label>
          </div>
          {channelOptions.map((option) => (
            <div
              key={option.value}
              className="flex items-center gap-2"
              onClick={() => selectChannel(option.value)}
            >
              <Radio
                color="primary"
                name="advanceSearchChannel"
                size="sm"
                checked={channelFilter === option.value}
                readOnly
              />
              <img src={option.iconSrc} className="w-4 h-4" />
              <label className="label-text-alt">{option.label}</label>
            </div>
          ))}
        </div>
      </div>
      <Divider className="m-0" />
      <div className="form-control gap-1">
        <label className="label-text font-sans font-bold">
          {t('filters.date')}
        </label>
        <Dropdown>
          <Dropdown.Toggle
            button={false}
            className={`input input-sm input-bordered flex items-center justify-between gap-2 border rounded shadow-sm py-2 px-3 w-full ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
          >
            <span className={dateFilterType ? '' : 'text-gray-400'}>
              {dateFilterType === eDateFilterType.LAST_ACTION_AT
                ? t('filters.lastActionAt')
                : dateFilterType === eDateFilterType.CREATED_AT
                  ? t('filters.createdAt')
                  : t('filters.selectDateFilterType')}
            </span>
            <ChevronDownIcon className="w-6" />
          </Dropdown.Toggle>
          <Dropdown.Menu className="w-60 mt-0 z-30 max-h-[200px] overflow-y-auto overflow-x-hidden !block">
            <Dropdown.Item
              className="rounded-none hover:bg-primary hover:text-white"
              onClick={() => {
                setDateFilterType(eDateFilterType.CREATED_AT);
                (document.activeElement as HTMLElement)?.blur();
              }}
            >
              {t('filters.createdAt')}
            </Dropdown.Item>
            <Dropdown.Item
              className="rounded-none hover:bg-primary hover:text-white"
              onClick={() => {
                setDateFilterType(eDateFilterType.LAST_ACTION_AT);
                (document.activeElement as HTMLElement)?.blur();
              }}
            >
              {t('filters.lastActionAt')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        <div
          className={`input input-sm input-bordered flex items-center gap-2 border rounded shadow-sm py-2 px-3 w-full ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
        >
          <CalendarIcon className="w-5 h-5" />
          <DatePicker
            selectsRange
            placeholderText={t('filters.selectADate')}
            popperClassName="ReactDatepicker_Popper"
            wrapperClassName="flex-1"
            startDate={startDate}
            endDate={endDate}
            maxDate={new Date()}
            onChange={(update) => {
              const [startDateUpdate, endDateUpdate] = update;
              const startDateForFilter = startDateUpdate || undefined;
              const endDateForFilters = endDateUpdate || undefined;

              setDateRange([startDateForFilter, endDateForFilters]);
            }}
          >
            <div className="flex w-full justify-center">
              <Button
                size="xs"
                variant="link"
                onClick={() => {
                  setDateRange([new Date(), new Date()]);
                }}
              >
                {t('filters.todayLeads')}
              </Button>
            </div>
          </DatePicker>
          {(startDate || endDate) ? (
            <XMarkIcon
              className="w-6 cursor-pointer"
              onClick={() => setDateRange([undefined, undefined])}
            />
          ) : (
            <ChevronDownIcon className="w-6" />
          )}
        </div>
        <label className="label py-0">
          <span className="label-text-alt opacity-70">
            {dateFilterType === eDateFilterType.LAST_ACTION_AT
              ? t('filters.selectLeadLastActionDate')
              : dateFilterType === eDateFilterType.CREATED_AT
                ? t('filters.selectLeadCreationDate')
                : t('filters.selectDateFilterType')}
          </span>
        </label>
      </div>
      <Divider className="m-0" />
      <div className="form-control gap-1">
        <label className="label-text font-sans font-bold">
          {t('advanceSearch.funnel')}
        </label>
        <Dropdown>
          <Dropdown.Toggle
            button={false}
            className={`input input-sm input-bordered flex items-center justify-between gap-2 border rounded shadow-sm py-2 px-3 w-full ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
          >
            {funnelDropdownTitle}
            <ChevronDownIcon className="w-6" />
          </Dropdown.Toggle>
          <Dropdown.Menu className="form-control flex-nowrap w-52 max-h-52 mt-2 rounded-md p-0 overflow-x-hidden overflow-y-scroll z-10">
            <Dropdown.Item
              className="rounded-none hover:bg-primary hover:text-white"
              onClick={() => {
                setFunnelForFilters(null);
                setStageForFilters(null);
                setStages([]);
              }}
            >
              {t('filters.all')}
            </Dropdown.Item>
            {funnels?.map((funnel) => (
              <Dropdown.Item
                className="rounded-none hover:bg-primary hover:text-white"
                key={funnel.id}
                onClick={() => {
                  getStages(funnel.id);
                  setFunnelForFilters(funnel);
                  setStageForFilters(null);
                }}
              >
                {funnel.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <label className="label py-0">
          <span className="label-text-alt opacity-70">
            {t('advanceSearch.selectFunnel')}
          </span>
        </label>
      </div>
      <Divider className="m-0" />
      <div className="form-control gap-1">
        <label className="label-text font-sans font-bold">
          {t('advanceSearch.stage')}
        </label>
        {loadingStages ? (
          <Skeleton className="w-full h-8 rounded" />
        ) : (
          <Dropdown>
            <Dropdown.Toggle
              button={false}
              className={`input input-sm input-bordered flex items-center justify-between gap-2 border rounded shadow-sm py-2 px-3 w-full ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
            >
              {stageDropdownTitle}
              {funnelForFilters ? <ChevronDownIcon className="w-6" /> : null}
            </Dropdown.Toggle>
            {stages.length && funnelForFilters?.id !== NIL ? (
              <Dropdown.Menu className="form-control flex-nowrap w-52 max-h-52 mt-2 rounded-md p-0 overflow-x-hidden overflow-y-scroll z-10">
                <Dropdown.Item
                  className="rounded-none hover:bg-primary hover:text-white"
                  onClick={() => {
                    setStageForFilters(null);
                  }}
                >
                  {t('filters.all')}
                </Dropdown.Item>
                {stages?.map((stage) => (
                  <Dropdown.Item
                    className="rounded-none hover:bg-primary hover:text-white"
                    key={stage.id}
                    onClick={() => {
                      setStageForFilters(stage);
                    }}
                  >
                    {stage.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            ) : null}
          </Dropdown>
        )}
        <label className="label py-0">
          <span className="label-text-alt opacity-70">
            {t('advanceSearch.selectStage')}
          </span>
        </label>
      </div>

      <Divider className="m-0" />
      <div className="form-control gap-1">
        <label className="label-text font-sans font-bold">
          {t('filters.users')}
        </label>
        <Dropdown vertical="top">
          <Dropdown.Toggle
            button={false}
            className={`input input-sm input-bordered flex items-center justify-between gap-2 border rounded shadow-sm py-2 px-3 w-full ${isDarkMode ? 'bg-base-100' : 'bg-secondary'}`}
          >
            {leadAssignedUserDropdownTitle}
            <ChevronDownIcon className="w-6" />
          </Dropdown.Toggle>
          <Dropdown.Menu className="form-control flex-nowrap w-52 max-h-52 mt-2 rounded-md p-0 overflow-x-hidden overflow-y-scroll z-10">
            <Dropdown.Item
              className="rounded-none hover:bg-primary hover:text-white"
              onClick={() => {
                setAssignedUserFilters(null);
              }}
            >
              {t('filters.all')}
            </Dropdown.Item>
            <Dropdown.Item
              className="rounded-none hover:bg-primary hover:text-white"
              onClick={() => {
                setAssignedUserFilters({
                  name: t('filters.notAssigned'),
                  authId: 'NONE',
                });
              }}
            >
              {t('filters.notAssigned')}
            </Dropdown.Item>
            {Object.values(workspaceInfo?.users ?? {}).map((user) => (
              <Dropdown.Item
                className="rounded-none hover:bg-primary hover:text-white"
                key={user.authId}
                onClick={() => {
                  setAssignedUserFilters(user);
                }}
              >
                {user.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <label className="label py-0">
          <span className="label-text-alt opacity-70">
            {t('advanceSearch.selectUser')}
          </span>
        </label>
      </div>

    </div>
  );
}
